/**
 * Fit Creamery custom — fc-overrides.js
 *
 * Shared helper layer for our customizations, plus the place for small
 * behaviour tweaks that are not big enough to earn their own fc-*.js file.
 *
 * WHY THIS EXISTS: Eurus boots Alpine late and ajax-injects the cart drawer
 * after load, so "wait for DOMContentLoaded and query the DOM" is unreliable.
 * fc-hide-floating-on-cart.js worked that out already; rather than copy its
 * retry boilerplate into every new file, it is generalised here as `FC.ready`.
 *
 * Exposes a single global: `window.FC`.
 *
 * Standalone; safe to keep across Eurus theme updates.
 */
(function () {
  'use strict';

  var FC = (window.FC = window.FC || {});

  /**
   * Run `fn` once Alpine has booted and its stores are registered.
   *
   * Alpine may initialise before or after this deferred script runs, so we
   * listen for `alpine:initialized` AND poll — whichever lands first wins.
   * Polling is capped at ~10s so a page without Alpine does not spin forever.
   *
   * @param {function} fn        called once, with no arguments
   * @param {string}   [storeName] if given, also wait for this Alpine store to exist
   */
  FC.ready = function (fn, storeName) {
    var done = false;

    function attempt() {
      if (done) return true;
      if (!(window.Alpine && Alpine.store && typeof Alpine.effect === 'function')) return false;
      if (storeName && !Alpine.store(storeName)) return false;
      done = true;
      try {
        fn();
      } catch (e) {
        // Never let one customization break the rest of the page.
        console.error('[FC] ready handler failed:', e);
      }
      return true;
    }

    document.addEventListener('alpine:initialized', attempt);

    function poll() {
      if (attempt()) return;
      var attempts = 0;
      var timer = setInterval(function () {
        if (attempt() || ++attempts > 40) clearInterval(timer);
      }, 250);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', poll);
    } else {
      poll();
    }
  };

  /**
   * Observe the theme's cart drawer open/closed state.
   *
   * Wraps the same reactive signal fc-hide-floating-on-cart.js uses:
   * `Alpine.store('xMiniCart').open`. Reading `.open` inside `Alpine.effect`
   * registers the dependency, so `fn` re-runs on every open and close.
   *
   * @param {function(boolean)} fn receives the new open state
   */
  FC.onCartToggle = function (fn) {
    FC.ready(function () {
      Alpine.effect(function () {
        fn(!!Alpine.store('xMiniCart').open);
      });
    }, 'xMiniCart');
  };

  /**
   * Refresh the mini-cart from the server.
   *
   * This is the theme's own cart-refresh routine (also used by product-cart.js).
   * Call it after any direct /cart/*.js write so the drawer reflects reality —
   * and note the bfcache caveat recorded in product-bundler.js: Alpine's
   * `alpine:init` listener does NOT fire on a back-navigation restore, so a
   * `pageshow` handler checking `event.persisted` should call this too.
   */
  FC.reloadCart = function () {
    FC.ready(function () {
      var store = Alpine.store('xMiniCart');
      if (store && typeof store.reLoad === 'function') store.reLoad();
    }, 'xMiniCart');
  };

  /**
   * Initialise `initFn` against every element matching `selector`, now and
   * after any future DOM change — exactly once per element.
   *
   * WHY: xMiniCart.reLoad() refreshes the drawer with
   * `sectionElement.innerHTML = ...` on EVERY cart mutation (see theme.js,
   * `reLoad`). Server-rendered markup returns by itself, but every listener,
   * interval and piece of element state attached to the old nodes is gone.
   * Wrapping reLoad does not help: its DOM write happens after an async fetch
   * resolves, so there is no reliable moment to hook. Observing the document
   * and re-initialising whatever is new is the approach that survives it.
   *
   * `initFn` must be safe to run on a fresh element with no memory of the old
   * one — persist anything that must outlive a re-render (see the reservation
   * timer, which uses sessionStorage for exactly this reason).
   *
   * @param {string} selector
   * @param {function(Element)} initFn
   */
  FC.hydrate = function (selector, initFn) {
    function scan() {
      var nodes = document.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.getAttribute('data-fc-hydrated') === '1') continue;
        el.setAttribute('data-fc-hydrated', '1');
        try {
          initFn(el);
        } catch (e) {
          console.error('[FC] hydrate failed for ' + selector + ':', e);
        }
      }
    }

    function watch() {
      scan();
      var pending = null;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(scan, 80);
      }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', watch);
    } else {
      watch();
    }
  };

  /* ── Same-day shipping countdown ─────────────────────────
   *
   * Behavioural twin of initShippingCountdown in
   * assets/section-custom-product-page.js. That implementation could not be
   * reused: it sits inside a closed IIFE and its file only loads on the product
   * template. Kept deliberately in step with it, including the ?ship_now=HH:MM
   * preview override — if you change the rules there, change them here.
   */

  // Seconds since midnight in an IANA timezone. Re-read each tick so DST is
  // handled automatically.
  function secondsInTz(tz) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(new Date());
    var o = {};
    parts.forEach(function (p) { o[p.type] = p.value; });
    var h = parseInt(o.hour, 10) % 24; // '24' → 0 at midnight
    return h * 3600 + parseInt(o.minute, 10) * 60 + parseInt(o.second, 10);
  }

  function formatRemaining(totalMinutes) {
    var h = Math.floor(totalMinutes / 60);
    var m = totalMinutes % 60;
    if (h > 0) return h + (h === 1 ? ' hr ' : ' hrs ') + m + (m === 1 ? ' min' : ' mins');
    return m + (m === 1 ? ' min' : ' mins');
  }

  // Simulate the warehouse clock from the URL, matching the PDP: ?ship_now=17:30
  function previewSeconds() {
    var q = new URLSearchParams(window.location.search).get('ship_now');
    if (!q) return null;
    var m = q.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    var ph = parseInt(m[1], 10), pm = parseInt(m[2], 10);
    if (ph < 0 || ph > 23 || pm < 0 || pm > 59) return null;
    return ph * 3600 + pm * 60;
  }

  FC.hydrate('[data-fc-countdown]', function (el) {
    var tz = el.getAttribute('data-tz') || 'America/Chicago';
    var startHour = parseInt(el.getAttribute('data-start'), 10); if (isNaN(startHour)) startHour = 0;
    var stopHour = parseInt(el.getAttribute('data-stop'), 10); if (isNaN(stopHour)) stopHour = 24;
    var withinHours = parseInt(el.getAttribute('data-within'), 10); if (isNaN(withinHours)) withinHours = 0;
    var before = el.getAttribute('data-before') || 'Order within {time} for same-day shipping';
    var fallback = el.getAttribute('data-fallback') || '';
    var textEl = el.querySelector('.fc-ship-text');
    if (!textEl) return;

    var preview = previewSeconds();
    var inEditor = !!(window.Shopify && window.Shopify.designMode);

    // Append text, wrapping any "*" in a faint footnote span — the fallback
    // copy is "Delivered in 2-3 days*" and the PDP renders that asterisk
    // smaller and dimmer. Mirrors appendText in section-custom-product-page.js.
    function appendText(parent, str) {
      var parts = String(str).split('*');
      parts.forEach(function (part, i) {
        if (part) parent.appendChild(document.createTextNode(part));
        if (i < parts.length - 1) {
          var ast = document.createElement('span');
          ast.className = 'fc-ship-ast';
          ast.textContent = '*';
          parent.appendChild(ast);
        }
      });
    }

    function paint(remMin) {
      var seg = before.split('{time}');
      textEl.textContent = '';
      appendText(textEl, seg[0] || '');
      var clock = document.createElement('span');
      clock.className = 'fc-ship-clock';
      clock.textContent = formatRemaining(remMin);
      textEl.appendChild(clock);
      appendText(textEl, seg.slice(1).join('{time}') || '');
    }

    // Whenever the countdown can't run, show the plain delivery estimate
    // instead of nothing. Mirrors showFallback in section-custom-product-page.js.
    function showFallback() {
      if (!fallback) { el.hidden = true; return; }
      textEl.textContent = '';
      appendText(textEl, fallback);
      el.hidden = false;
    }

    function render() {
      var startSec = startHour * 3600, stopSec = stopHour * 3600, nowSec;

      // Editor demo, so the block is visible and stylable regardless of time.
      if (preview === null && inEditor) { paint(107); el.hidden = false; return; }

      if (preview !== null) {
        nowSec = preview;
      } else {
        // Invalid tz → no clock, so no countdown. The fallback is
        // time-independent, so it is still safe to show.
        try { nowSec = secondsInTz(tz); }
        catch (e) { showFallback(); return; }
      }

      // Outside fulfillment hours (warehouse closed) → fallback.
      if (nowSec < startSec || nowSec >= stopSec) { showFallback(); return; }

      // Inside hours but before the lead-in → countdown too long to feel
      // urgent, so show the fallback rather than an empty slot.
      if (withinHours > 0 && (stopSec - nowSec) > withinHours * 3600) { showFallback(); return; }

      paint(Math.ceil((stopSec - nowSec) / 60)); // ceil: never "0 mins" with seconds left
      el.hidden = false;
    }

    render();
    setInterval(render, 30000);
  });

  /* ── Reservation timer ───────────────────────────────────
   *
   * Styled after the checkout block but worded differently on purpose: the
   * cart reserves "your products" for 20 minutes, checkout reserves "your
   * cart" for 10. Two separate reservations read as intentional; one timer
   * that resets on the way to checkout reads as broken.
   *
   * The deadline lives in sessionStorage because the drawer is destroyed and
   * rebuilt on every cart mutation — an in-memory countdown would restart
   * several times a session.
   */
  var RESERVE_KEY = 'fc.reserveUntil';

  FC.hydrate('[data-fc-reserve]', function (el) {
    var minutes = parseInt(el.getAttribute('data-minutes'), 10);
    if (isNaN(minutes) || minutes <= 0) minutes = 20;
    var expiredText = el.getAttribute('data-expired') || '';
    var timeEl = el.querySelector('[data-fc-reserve-time]');
    var labelEl = el.querySelector('.fc-reserve-label');
    var label = el.getAttribute('data-label');
    if (label && labelEl) labelEl.textContent = label;
    if (!timeEl) return;

    var until = parseInt(sessionStorage.getItem(RESERVE_KEY) || '', 10);
    if (isNaN(until)) {
      until = Date.now() + minutes * 60000;
      try { sessionStorage.setItem(RESERVE_KEY, String(until)); } catch (e) { /* private mode */ }
    }

    function tick() {
      var remaining = Math.round((until - Date.now()) / 1000);
      if (remaining > 0) {
        var m = Math.floor(remaining / 60);
        var s = remaining % 60;
        timeEl.textContent = m + ':' + (s < 10 ? '0' + s : s);
        el.hidden = false;
        return;
      }
      // Expired. Show the configured message, or hide rather than invent copy.
      clearInterval(timer);
      if (expiredText) {
        el.classList.add('fc-reserve--expired');
        if (labelEl) labelEl.textContent = expiredText;
        timeEl.textContent = '';
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    }

    var timer = setInterval(tick, 1000);
    tick();
  });

  /* ── Checkout button label ───────────────────────────────
   *
   * "Check out" → "Secure Checkout".
   *
   * WHY JS AND NOT A LOCALE EDIT: the label comes from the translation key
   * `sections.cart.checkout`, so the obvious fix is one line in
   * locales/en.default.json. That was the original plan, and it is wrong here —
   * Shopify locale files are strict JSON and cannot carry comments, so such an
   * edit could not be tagged FC-CUSTOM. The grep in CUSTOMIZATIONS.md is the
   * authoritative inventory of what a Eurus update destroys, and an untaggable
   * edit is one that silently reverts. Doing it here keeps every BSS file
   * untouched and the inventory honest.
   *
   * Replacing text nodes rather than innerHTML keeps whatever structure
   * `button-label` produced — some of its branches render the label twice for
   * a hover effect, and both copies need updating.
   */
  FC.hydrate('#checkout-submit', function (btn) {
    var LABEL = 'Secure checkout';
    var walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [], n;
    while ((n = walker.nextNode())) { if (n.nodeValue.trim()) nodes.push(n); }
    nodes.forEach(function (t) { t.nodeValue = LABEL; });

    // Loading spinner, matching the bundler's add-to-cart buttons. Checkout is
    // a navigation, so the spinner should stay up until the page changes —
    // with two escape hatches so it can never be left spinning on a page the
    // shopper is still looking at:
    //   • a timeout, in case checkout is blocked (unaccepted terms, etc.)
    //   • pageshow, for a bfcache restore when they press Back
    if (!btn.querySelector('.fc-btn-spinner')) {
      var spinner = document.createElement('span');
      spinner.className = 'fc-btn-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      btn.appendChild(spinner);
    }

    // "View Cart" → "View cart", so the pair reads in one sentence case.
    var row = btn.parentElement;
    var viewCart = row && row.querySelector('a.button');
    if (viewCart) {
      var vw = document.createTreeWalker(viewCart, NodeFilter.SHOW_TEXT, null, false);
      var vn;
      while ((vn = vw.nextNode())) {
        if (vn.nodeValue.trim()) vn.nodeValue = 'View cart';
      }
    }

    var reset = function () { btn.classList.remove('is-loading'); };

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      btn.classList.add('is-loading');
      setTimeout(reset, 8000);
    });

    window.addEventListener('pageshow', function (e) { if (e.persisted) reset(); });
  });

  /* ── Cart savings wording ────────────────────────────────
   *
   * Eurus renders `sections.cart.total_savings_html` — "You've saved $127.92
   * USD!" — with the amount already interpolated, so there is no setting to
   * change and a locale edit could not be tagged FC-CUSTOM (strict JSON, no
   * comments; see the checkout-label note above). Rewriting the node keeps the
   * change here, and pulls the amount back out of the rendered string rather
   * than recomputing it, so it can never disagree with the cart.
   */
  FC.hydrate('#CartDrawer div[role="status"]', function (row) {
    var isSavingsRow = !!row.querySelector('s#total-price');

    if (isSavingsRow) {
      var p = row.querySelector('p');
      var struck = row.querySelector('s#total-price');

      // Prefer the server-rendered figures, which use cart.total_discount —
      // what checkout actually applies. Eurus's own numbers fold in
      // compare_at_price markdowns and overstate the saving; see the note in
      // fc-cart-extras.liquid.
      var figures = document.querySelector('[data-fc-cart-figures]');
      if (figures) {
        var discount = parseInt(figures.getAttribute('data-discount'), 10);

        // No real discount: Eurus may still be showing a compare-at "saving".
        if (!discount || discount <= 0) {
          row.style.display = 'none';
          return;
        }

        if (p) p.textContent = "You're saving " + figures.getAttribute('data-discount-money');
        if (struck) struck.textContent = figures.getAttribute('data-original-money');
        row.style.display = '';
        return;
      }

      // Fallback if the figures row is ever missing: keep Eurus's number but
      // apply our wording, rather than showing nothing.
      if (p) {
        var amount = (p.textContent || '').match(/[^\s]*\d[\d.,]*/);
        if (amount) p.textContent = "You're saving " + amount[0];
      }
      if (struck) struck.textContent = stripCurrency(struck.textContent);
      return;
    }

    // Total row: add the colon and strip the currency from the amount.
    var label = row.querySelector('p.h4');
    if (label && !/:$/.test(label.textContent.trim())) {
      label.textContent = label.textContent.trim() + ':';
    }
    var price = row.querySelector('p.h4#total-price');
    if (price) price.textContent = stripCurrency(price.textContent);
  });

  // "$192.00 USD" → "$192.00". Eurus renders these with money_with_currency and
  // there is no per-block setting for it, so the suffix is trimmed here rather
  // than recomputed — the number itself is never touched.
  function stripCurrency(text) {
    return String(text).replace(/\s*[A-Z]{3}\s*$/, '').trim();
  }

  /* ── Quantity and remove feedback ────────────────────────
   *
   * Replaces both overlay treatments (Eurus's spinner and the skeleton that
   * briefly replaced it) with feedback on the control the shopper touched.
   *
   * Delegated from the document rather than bound per row: both carts are
   * re-rendered on every cart mutation, so a per-element listener would die on
   * the very interaction it is reporting. This listener is registered once and
   * keeps working for every row that ever renders, on either surface.
   *
   * The two surfaces name the same things differently — the drawer's bin sits
   * inside the quantity block and its input is `.cart-qty`, the cart page's bin
   * is `#remove-{key}` and its input is `cart-item-qty-{index}` — so each
   * lookup below accepts either spelling.
   */
  document.addEventListener('click', function (e) {
    var cart = e.target.closest && e.target.closest('#CartDrawer, #main-cart-items');
    if (!cart) return;

    var row = e.target.closest('.cart-item');
    if (!row) return;

    // Bin: tip the icon, then fade the row while the request is in flight.
    var bin = e.target.closest('.cart-item__quantity > div > button, button[id^="remove-"]');
    if (bin) {
      bin.classList.add('fc-bin-tip');
      row.classList.add('fc-row-removing');
      setTimeout(function () { bin.classList.remove('fc-bin-tip'); }, 500);
      return;
    }

    // Stepper: pop the number. Fires on click rather than on the value landing,
    // so the response is immediate even though the row is about to be replaced.
    var stepBtn = e.target.closest('button');
    if (!stepBtn) return;
    var control = stepBtn.parentElement;
    var input = control && control.querySelector('.cart-qty, input[class*="cart-item-qty-"]');
    if (!input) return;

    input.classList.remove('fc-qty-pop');
    void input.offsetWidth; // reflow, so a rapid second tap restarts it
    input.classList.add('fc-qty-pop');
    setTimeout(function () { input.classList.remove('fc-qty-pop'); }, 400);
  }, true);

  /* ── Low stock notice date ───────────────────────────────
   *
   * Painted client-side in the warehouse timezone so it cannot go stale in a
   * tab left open past midnight. The Liquid-rendered value in the markup is
   * the no-JS fallback.
   */
  FC.hydrate('[data-fc-lowstock]', function (el) {
    var dateEl = el.querySelector('[data-fc-lowstock-date]');
    if (!dateEl) return;
    var tz = el.getAttribute('data-tz') || undefined;

    function paintDate() {
      try {
        dateEl.textContent = new Intl.DateTimeFormat('en-US', {
          timeZone: tz, weekday: 'long', month: 'long', day: 'numeric'
        }).format(new Date());
      } catch (e) { /* keep the Liquid fallback */ }
    }

    paintDate();
    setInterval(paintDate, 60000);
  });

  /* ── Cart page: table semantics ──────────────────────────
   *
   * fc-overrides.css turns the /cart table into a grid, because `order` — the
   * only thing that clusters the mix lines — needs grid items. Chrome and
   * Firefox derive table roles from the computed `display`, so that conversion
   * silently strips the row/cell semantics a screen reader relies on. Stamping
   * the roles back on restores them without touching BSS markup.
   *
   * Our own rows already carry role="row" from fc-cart-extras.liquid; their
   * cells are picked up by the same pass.
   */
  FC.hydrate('#main-cart-items table.cart-items', function (table) {
    table.setAttribute('role', 'table');

    var bodies = table.querySelectorAll('tbody');
    for (var b = 0; b < bodies.length; b++) bodies[b].setAttribute('role', 'rowgroup');

    var rows = table.querySelectorAll('tbody > tr');
    for (var r = 0; r < rows.length; r++) {
      rows[r].setAttribute('role', 'row');
      var cells = rows[r].children;
      for (var c = 0; c < cells.length; c++) {
        if (cells[c].tagName === 'TD') cells[c].setAttribute('role', 'cell');
      }
    }
  });

  /* ── Cart page: summary wording ──────────────────────────
   *
   * The drawer's totals treatment, applied to the sidebar. Two things happen
   * here that the drawer also does, for the same reasons — see the drawer's
   * "Cart savings wording" block above:
   *
   *   1. The currency suffix comes off. Eurus renders every figure in this
   *      panel with money_with_currency, ignoring `currency_code_enable`, so
   *      "$192.00 USD" sat next to line items reading "$192.00".
   *   2. The saving is restated from cart.total_discount rather than Eurus's
   *      own number, which folds compare_at_price markdowns in with real
   *      discounts and so overstates it.
   *
   * The figures come from the same [data-fc-cart-figures] row the drawer uses;
   * both surfaces render one and both carry identical values, so whichever the
   * query finds first is correct.
   */
  FC.hydrate('#main-cart-items #js-contents', function (box) {
    // Every money figure in the panel. `p.h4` also matches the "Total" label,
    // which has no suffix to strip — the pass is a no-op on it. All of these
    // hold text only, so setting textContent cannot destroy structure.
    var amounts = box.querySelectorAll('#total-price, p.h4, div.flex > p:last-child');
    for (var i = 0; i < amounts.length; i++) {
      amounts[i].textContent = stripCurrency(amounts[i].textContent);
    }

    // The savings row is the one holding the struck original. The cart-level
    // discount block is also role="status" but has no <s>, so this cannot
    // catch it by mistake.
    var struck = box.querySelector('div[role="status"] > s#total-price');
    var row = struck && struck.parentElement;
    if (!row) return;
    var p = row.querySelector('p');

    var figures = document.querySelector('[data-fc-cart-figures]');
    if (figures) {
      var discount = parseInt(figures.getAttribute('data-discount'), 10);

      // No real discount: Eurus may still be showing a compare-at "saving".
      if (!discount || discount <= 0) {
        row.style.display = 'none';
        return;
      }

      if (p) p.textContent = "You're saving " + figures.getAttribute('data-discount-money');
      struck.textContent = figures.getAttribute('data-original-money');
      row.style.display = '';
      return;
    }

    // Fallback if the figures row is ever missing: keep Eurus's number but
    // apply our wording, rather than showing nothing.
    if (p) {
      var amount = (p.textContent || '').match(/[^\s]*\d[\d.,]*/);
      if (amount) p.textContent = "You're saving " + amount[0];
    }
  });

  /* ── Let the drawer open on /cart ────────────────────────
   *
   * Eurus wraps the ENTIRE body of `xMiniCart.openCart()` in
   * `if (window.location.pathname != '/cart')`, so the cart icon does nothing
   * on the cart page. That is deliberate on their part — you are already
   * looking at the cart — but the two surfaces are no longer the same thing
   * here, and the icon appearing dead is worse than a redundant drawer.
   *
   * The guard cannot be configured away, so the store method is wrapped: off
   * /cart it delegates to Eurus untouched, and on /cart it does the four
   * things the guarded branch would have done. Kept to those four deliberately
   * — this is a re-implementation of BSS code, and the less of it there is,
   * the less there is to drift.
   *
   * THE DRAWER ISN'T ON THE PAGE (fixed 2026-10-09). sections/header.liquid
   * only lazy-loads the drawer markup `unless template == 'cart'`, so on /cart
   * there was no drawer to show. Opening anyway raised the dimmed backdrop,
   * which locks scrolling, with nothing on top of it and no close button —
   * the page froze. So on /cart the drawer is fetched on first open, the same
   * request Eurus's renderAjax makes, and put where Eurus would put it: inside
   * #x-header-container, whose z-[60] while open is what lifts the drawer
   * above the sticky header. Nothing is raised until the markup is in; if the
   * fetch fails the icon does nothing, which beats a frozen page.
   */
  var onCartPage = function () {
    return window.location.pathname.replace(/\/+$/, '') === '/cart';
  };

  var drawerLoad = null;

  function ensureDrawer() {
    if (document.getElementById('CartDrawer')) return Promise.resolve(true);
    if (drawerLoad) return drawerLoad;

    var container = document.getElementById('x-header-container');
    if (!container) return Promise.resolve(false);

    drawerLoad = fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (res) { return res.json(); })
      .then(function (json) {
        var doc = new DOMParser().parseFromString(json['cart-drawer'] || '', 'text/html');
        var source = doc.querySelector('#ajax-loading-cart');
        if (!source || !source.querySelector('#CartDrawer')) return false;

        var host = document.createElement('div');
        host.setAttribute('data-fc-cart-drawer-host', '');
        host.innerHTML = source.innerHTML;
        container.appendChild(host);

        // Alpine initialises the new x-data root from its mutation observer;
        // give it a frame before anything toggles `open`, so x-show sees a
        // change and plays the slide-in.
        return new Promise(function (resolve) {
          requestAnimationFrame(function () { resolve(true); });
        });
      })
      .catch(function (e) {
        console.error('[FC] cart drawer load failed:', e);
        return false;
      })
      .then(function (ok) {
        if (!ok) drawerLoad = null;
        return ok;
      });

    return drawerLoad;
  }

  FC.ready(function () {
    var store = Alpine.store('xMiniCart');
    if (!store || typeof store.openCart !== 'function' || store.fcOpenPatched) return;

    var native = store.openCart.bind(store);

    store.openCart = function () {
      if (!onCartPage()) return native();

      ensureDrawer().then(function (ok) {
        if (!ok) return;

        var quickView = Alpine.store('xQuickView');
        if (quickView && quickView.show) quickView.show = false;

        var header = document.getElementById('sticky-header');
        if (header) header.classList.remove('on-scroll-up-animation', 'header-up');

        // The dimmed backdrop, which the drawer needs to read as a layer.
        if (window.innerWidth < 768 || store.type === 'drawer') {
          setTimeout(function () {
            var popup = Alpine.store('xPopup');
            if (popup) popup.open = true;
          }, 50);
        }

        store.open = true;
      });
    };

    store.fcOpenPatched = true;
  }, 'xMiniCart');

  /* Keep that drawer current. On /cart, Eurus's getSectionsToRender() lists the
   * page's sections and not the drawer, so a change made from either surface
   * re-rendered the page and left the drawer showing the old cart. Once the
   * drawer exists it is added to the list — in place of any listed section
   * not on the page (the cart template's upsell is disabled), because Shopify
   * renders at most five sections per request and the page list is already
   * five long. */
  FC.ready(function () {
    var helper = Alpine.store('xCartHelper');
    if (!helper || typeof helper.getSectionsToRender !== 'function' || helper.fcSectionsPatched) return;

    var native = helper.getSectionsToRender.bind(helper);

    helper.getSectionsToRender = function () {
      var list = native();
      if (!onCartPage() || !document.getElementById('CartDrawer')) return list;
      if (list.some(function (s) { return s.id === 'cart-drawer'; })) return list;

      list = list.filter(function (s) {
        return s.selector.split(',').some(function (sel) { return document.querySelector(sel); });
      });
      if (list.length >= 5) return list;

      list.push({ id: 'cart-drawer', selector: '#CartDrawer' });
      return list;
    };

    helper.fcSectionsPatched = true;
  }, 'xCartHelper');

  /* ── Auto-ship toggle ────────────────────────────────────
   *
   * Markup: snippets/fc-cart-autoship.liquid, on both cart surfaces.
   *
   * Puts every ice cream mix line onto a selling plan, or takes them all off
   * one. `/cart/change.js` accepts a `selling_plan`, so a line can be
   * converted in place instead of being removed and re-added — which is what
   * keeps line order (and therefore checkout order) intact. It insists on
   * `line` rather than `id` for that parameter, and on the quantity being
   * restated, so the lines are converted one at a time.
   *
   * The cart is re-read between changes rather than the line indices being
   * worked out up front: converting a line can merge it into another line
   * holding the same variant on the same plan — a cart with both tracks has
   * exactly that — and a merge shifts every index after it. Asking the
   * returned cart for the next line still on the wrong plan is immune to it.
   */

  function cartFetch(url, body) {
    var opts = { method: body ? 'POST' : 'GET', headers: { Accept: 'application/json' } };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || res.statusText);
        return data;
      });
    });
  }

  function sellingPlanOf(item) {
    return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? item.selling_plan_allocation.selling_plan.id
      : null;
  }

  /** Index of the first mix line not already on `wanted`, or -1. */
  function nextUnconverted(cart, mixType, wanted) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_type !== mixType) continue;
      if (sellingPlanOf(cart.items[i]) !== wanted) return i;
    }
    return -1;
  }

  function applyPlan(cart, mixType, wanted, pass) {
    var idx = nextUnconverted(cart, mixType, wanted);
    if (idx === -1) return Promise.resolve(cart);

    // A backstop, not a limit: one pass per mix line is what this costs, and a
    // cart cannot hold more mix lines than the shopper picked flavours.
    if (pass > 20) {
      console.warn('[FC] auto-ship: giving up after ' + pass + ' line changes');
      return Promise.resolve(cart);
    }

    return cartFetch('/cart/change.js', {
      line: idx + 1,
      quantity: cart.items[idx].quantity,
      selling_plan: wanted
    }).then(function (next) {
      return applyPlan(next, mixType, wanted, pass + 1);
    });
  }

  // Module-level, not per element: every cart mutation re-renders the block,
  // so a flag living on the element would be thrown away mid-request.
  var autoshipBusy = false;

  /* Whether the shopper has folded the block.
   *
   * Module-level for the same reason, and deliberately NOT persisted: the
   * block always starts expanded, so the offer is showing every time the cart
   * is opened, and folding is something the shopper does rather than something
   * they inherit. This variable dies with the page, which is exactly that —
   * while still surviving the re-render that follows every quantity change,
   * which a DOM-held flag would not.
   */
  var autoshipFolded = false;

  function applyAutoshipFold(el, animate) {
    if (!animate) el.classList.add('fc-as--noanim');
    el.classList.toggle('is-shut', autoshipFolded);
    var band = el.querySelector('[data-fc-autoship-fold]');
    if (band) band.setAttribute('aria-expanded', autoshipFolded ? 'false' : 'true');
    if (!animate) {
      // Next frame, once the folded state has painted.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { el.classList.remove('fc-as--noanim'); });
      });
    }
  }

  FC.hydrate('[data-fc-autoship]', function (el) {
    var mixType = el.getAttribute('data-mix-type') || '';
    var planId = parseInt(el.getAttribute('data-plan'), 10);
    if (!mixType || !planId) return;

    // Restore the fold without animating: this element is new, and a block
    // that animates shut as it arrives reads as a glitch, not a state.
    applyAutoshipFold(el, false);

    el.addEventListener('click', function (e) {
      var fold = e.target.closest('[data-fc-autoship-fold]');
      if (fold) {
        e.preventDefault();
        autoshipFolded = !autoshipFolded;
        // Every instance, not just this one: on /cart the page and the drawer
        // each render the block, and they are two views of one thing.
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-fc-autoship]'),
          function (node) { applyAutoshipFold(node, node === el); }
        );
        return;
      }

      var btn = e.target.closest('[data-fc-autoship-mode]');
      if (!btn) return;
      e.preventDefault();

      if (btn.getAttribute('aria-pressed') === 'true') return; // already there
      if (autoshipBusy) return;

      var wanted = btn.getAttribute('data-fc-autoship-mode') === 'subscription' ? planId : null;

      autoshipBusy = true;
      el.classList.add('is-busy');

      function done() {
        autoshipBusy = false;
        el.classList.remove('is-busy');
      }

      cartFetch('/cart.js')
        .then(function (cart) { return applyPlan(cart, mixType, wanted, 0); })
        .then(function () {
          // The drawer re-renders itself from the server. The /cart page does
          // not: the plan changes its line rows, its progress track and its
          // totals, and Eurus's section refresh is its own internal routine —
          // so that surface is reloaded rather than patched. Reload whenever
          // the page is /cart, including a toggle made in the drawer on top
          // of it, since the page behind is stale either way.
          if (document.getElementById('main-cart-items')) {
            window.location.reload();
            return;
          }
          FC.reloadCart();
          done();
        })
        .catch(function (err) {
          console.error('[FC] auto-ship switch failed:', err);
          done();
        });
    });
  });

  /* ── "Add more flavors" ──────────────────────────────────
   *
   * Markup: snippets/fc-cart-flavor-adder.liquid, on both cart surfaces.
   *
   * Two jobs: fold the panel, and add one bag of a flavor the cart does not
   * hold yet. The Liquid decides WHICH flavors are offered and which selling
   * plan a new line should join — see its header — so all this does is post
   * what the button was rendered with.
   */

  /* Whether the shopper has opened the panel.
   *
   * Module-level rather than on the element, for the reason FC.hydrate
   * documents: every cart mutation replaces this markup, so a flag living in
   * the DOM would be thrown away by the shopper's own click. Deliberately not
   * persisted — the block renders closed, and this variable dies with the
   * page, which together is "closed on every fresh load, still open across the
   * re-render that follows an add".
   */
  var flavorsOpen = false;
  var flavorBusy = false;

  function applyFlavorFold(el, animate) {
    if (!animate) el.classList.add('fc-flav--noanim');
    el.classList.toggle('is-open', flavorsOpen);
    var trigger = el.querySelector('[data-fc-flavors-fold]');
    if (trigger) trigger.setAttribute('aria-expanded', flavorsOpen ? 'true' : 'false');
    if (!animate) {
      // Next frame, once the restored state has painted.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { el.classList.remove('fc-flav--noanim'); });
      });
    }
  }

  FC.hydrate('[data-fc-flavors]', function (el) {
    // Restore without animating: this element is new, and a panel that
    // animates open as it arrives reads as a glitch, not as a state.
    applyFlavorFold(el, false);

    el.addEventListener('click', function (e) {
      var fold = e.target.closest('[data-fc-flavors-fold]');
      if (fold) {
        e.preventDefault();
        flavorsOpen = !flavorsOpen;
        // Every instance, not just this one: on /cart the page and the drawer
        // each render the block, and they are two views of one thing.
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-fc-flavors]'),
          function (node) { applyFlavorFold(node, node === el); }
        );
        return;
      }

      var btn = e.target.closest('[data-fc-flavor-add]');
      if (!btn) return;
      e.preventDefault();

      if (flavorBusy) return;

      var variantId = parseInt(btn.getAttribute('data-fc-flavor-add'), 10);
      if (!variantId) return;

      // Absent on a one-time cart, which is the whole meaning of "no plan":
      // /cart/add.js wants the key left off, not set to null.
      var planId = parseInt(el.getAttribute('data-plan'), 10);
      var line = { id: variantId, quantity: 1 };
      if (planId) line.selling_plan = planId;

      flavorBusy = true;
      el.classList.add('is-busy');

      function done() {
        flavorBusy = false;
        el.classList.remove('is-busy');
      }

      cartFetch('/cart/add.js', { items: [line] })
        .then(function () {
          // Same split as the auto-ship block: the drawer re-renders itself
          // from the server, the /cart page does not — a new line changes its
          // rows, its progress track and its totals, and Eurus's section
          // refresh is its own internal routine. So that surface is reloaded
          // rather than patched, including when the add was made in the drawer
          // on top of it, since the page behind is stale either way.
          if (document.getElementById('main-cart-items')) {
            window.location.reload();
            return;
          }
          FC.reloadCart();
          done();
        })
        .catch(function (err) {
          console.error('[FC] add flavor failed:', err);
          done();
        });
    });
  });

  /**
   * Email address assembled in the browser.
   *
   * Theme text that would otherwise print our address carries a placeholder
   * link instead — `<a href="#fc-email">our email address</a>` — which this
   * swaps for the real address. Crawlers that read the raw HTML only ever see
   * the placeholder. The address is kept in pieces here for the same reason:
   * this file is public too.
   *
   * Used by the "Track my order" step of the support page's guided navigator
   * (templates/page.contact.json). A richtext setting can hold a link but not
   * a span with data attributes, hence the link as the marker.
   */
  FC.hydrate('a[href="#fc-email"]', function (el) {
    var address = ['hello', 'fitcreamery.com'].join(String.fromCharCode(64));
    el.replaceWith(document.createTextNode('"' + address + '"'));
  });
})();
